import { Injectable, OnDestroy } from "@angular/core";
import { BehaviorSubject, Observable, Subject, combineLatest } from "rxjs";
import {
  distinctUntilChanged,
  filter,
  map,
  takeUntil,
  tap,
} from "rxjs/operators";
import { MsalService, MsalBroadcastService } from "@azure/msal-angular";
import {
  AccountInfo,
  AuthenticationResult,
  EventMessage,
  EventType,
  InteractionRequiredAuthError,
  PopupRequest,
  RedirectRequest,
} from "@azure/msal-browser";
import { InteractionStatus } from "@azure/msal-browser";

@Injectable({ providedIn: "root" })
export class AuthService implements OnDestroy {
  private destroyed$ = new Subject<void>();

  private _account = new BehaviorSubject<AccountInfo | null>(null);

  /** Emits the current active account (or null) */
  readonly account$: Observable<AccountInfo | null> =
    this._account.asObservable();

  private _isAuthenticated = new BehaviorSubject<boolean>(false);

  /** Emits true when an account is present and MSAL is idle */
  readonly isAuthenticated$: Observable<boolean> = this._isAuthenticated
    .asObservable()
    .pipe(distinctUntilChanged());

  constructor(
    private msal: MsalService,
    private msalBroadcast: MsalBroadcastService
  ) {
    // 1) Update state once MSAL is idle (prevents startup race conditions)
    combineLatest([
      this.msalBroadcast.inProgress$.pipe(
        filter((s) => s === InteractionStatus.None)
      ),
      this.msalBroadcast.msalSubject$.pipe(
        // Only react to relevant auth events; also fires on app start with cached accounts
        filter(
          (e: EventMessage) =>
            e.eventType === EventType.LOGIN_SUCCESS ||
            e.eventType === EventType.SSO_SILENT_SUCCESS ||
            e.eventType === EventType.HANDLE_REDIRECT_END ||
            e.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
            e.eventType === EventType.LOGOUT_SUCCESS ||
            e.eventType === EventType.ACCOUNT_ADDED ||
            e.eventType === EventType.ACCOUNT_REMOVED
        ),
        tap((e) => {
          // If an auth result is present, set it as active
          const result = e.payload as AuthenticationResult | undefined;
          if (result?.account) {
            this.msal.instance.setActiveAccount(result.account);
          }
        })
      ),
    ])
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => this.refreshState());

    // 2) Also initialize once (covers first load)
    this.refreshState();
  }

  /** Synchronous check (useful in guards/components) */
  isAuthenticatedSync(): boolean {
    return this._isAuthenticated.value;
  }

  /** The currently active account (or null) */
  getActiveAccount(): AccountInfo | null {
    return this._account.value;
  }

  getEmail(): string | null {
    const account = this.getActiveAccount();
    if (!account) return null;

    const claims = account.idTokenClaims as any;

    return (
      claims?.email ||
      claims?.preferred_username ||
      claims?.upn ||
      (Array.isArray(claims?.emails) ? claims.emails[0] : null) ||
      null
    );
  }

  /** Start login */
  login(redirectTo?: string, req?: RedirectRequest) {
    const request: RedirectRequest = {
      ...req,
      // supply your API scopes here if you want them on first hop
      // scopes: ['api://<api-app-id>/access_as_user'],
      redirectStartPage: redirectTo ?? window.location.href,
    };
    return this.msal.loginRedirect(request);
  }

  /** Logout */
  logout(redirectTo?: string) {
    return this.msal.logoutRedirect({
      postLogoutRedirectUri: redirectTo ?? undefined,
    });
  }

  /**
   * Get an access token for given scopes.
   * Falls back to interactive login redirect if interaction is required.
   */
  async acquireToken(scopes: string[]): Promise<string> {
    const account =
      this.msal.instance.getActiveAccount() ??
      this.msal.instance.getAllAccounts()[0];
    if (!account) {
      // no account — trigger login to get one
      await this.login();
      throw new Error("Login initiated");
    }

    try {
      const result = await this.msal.instance.acquireTokenSilent({
        account,
        scopes,
      });
      return result.accessToken;
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError) {
        await this.msal.loginRedirect({ scopes });
        throw new Error("Login initiated for consent");
      }
      throw err;
    }
  }

  /** Internal: read MSAL cache and push current state */
  private refreshState() {
    let account = this.msal.instance.getActiveAccount();
    if (!account) {
      const all = this.msal.instance.getAllAccounts();
      if (all.length) {
        account = all[0];
        this.msal.instance.setActiveAccount(account);
      }
    }
    this._account.next(account ?? null);
    this._isAuthenticated.next(!!account);
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
